import React from 'react';

import {DatabaseContext} from '../database/DatabaseHandler';
import IParamScript from '../database/interfaces/paramScript/IParamScript';
import type IParamTypeDescriptor from '../database/interfaces/paramScript/IParamTypeDescriptor';
import type { ISizeParamTypeDescriptor } from '../database/interfaces/paramScript/IParamTypeDescriptor';
import type IUnitsTypeDescriptor from '../database/interfaces/paramScript/IUnitsTypeDescriptor';
import PrestaGlobals from '../common/PrestaGlobals';
import DimensionUtils from '../common/DimensionUtils';
import TParameters from './TParameters';
import PrefsApi from '../prefs/PrefsApi';
import {
    ALL_EDGE_KEYS,
    buildDecorEdgeOutput,
    cleanParamEdgeOutput,
    SIDE_KEYS,
    VISIBLE_EDGE_KEY,
    ZERO_GUID
} from "../configurator/cards/edgeKeys";
import {
    IRelationsParamInfo,
    mapRelationsWrapper,
    mergeRelationsWrappers
} from "../configurator/cards/backPassingRelations";
import { applyIndexRecord, orderNamesByApplyIndex, toApplyIndex } from "./buildOrder";
import { itemCount, itemEntries, itemsEmitKeyMap, resolveItems, TItemsModel } from "./paramItems";
import { isParamVisible, TVisibleWhen } from "./paramVisibility";
import { applyEffect, matchingEffects, TParamEffect } from "./paramEffects";
import { describeGuardNote, guardDimension, guardParameterValue } from "./paramGuards";
import { boardBoundMax, TBoardRef } from "./paramBounds";

/** Parameter context */
export const ParametersContext = React.createContext<{
    parameters: TParameters;
    updateParameters: (name: string, property: string, value: any) => void;
    generateParameterScripts: () => IParamScript[];
    generateStyleScript: () => any[];
    notifyParamChanged: (script: IParamScript) => void;
}>({
    parameters: {},
    updateParameters: () => {},
    generateParameterScripts: () => [],
    generateStyleScript: () => [],
    notifyParamChanged: () => {},
});

/** Egy paraméter Unity-stylescript metaadata, egyesítve: JSON-sorrend (applyIndex) + a Relations-
 *  transzform infója (relations) + a kimeneti feltétel (visibleWhen). A build-effekt tölti a DB
 *  paramScriptsből. */
type TEmitPaths = NonNullable<IParamTypeDescriptor["emitPaths"]>;

type TParamMeta = {
    applyIndex?: number;
    relations?: IRelationsParamInfo;
    flatten?: string;
    /** Composite mezők kimeneti átkulcsolása: {wrapper property-kulcs, mező-kulcs → kimeneti kulcsok}. */
    emitAs?: { property: string; map: Record<string, string[]> };
    /** CSAK a UI-é: a descriptor gyökerén álló `emitAs: []` — a paraméter ki sem megy a stylescriptbe. */
    uiOnly?: boolean;
    /** Az üres objektum-érték is kimegy (descriptor `emitEmpty`). */
    emitEmpty?: boolean;
    /** SZÁRMAZTATOTT kimeneti érték: {a saját property-kulcs, a descriptor `emitFrom` szabálya}. */
    emitFrom?: { property: string; rule: NonNullable<IParamTypeDescriptor["emitFrom"]> };
    /** A kimeneti érték SZÁM legyen, ne méret-string (descriptor `emitNumber`) — a property-kulccsal. */
    emitNumber?: string;
    /** EGY érték TÖBB útvonalra, útvonalanként külön entryben (descriptor `emitPaths`). */
    emitPaths?: TEmitPaths;
    /** Minden entrybe bekerülő állandó kulcsok (descriptor `emitConst`). */
    emitConst?: Record<string, string | number>;
    /** Az `emitConst` egység-kapuja és a saját property-kulcs, amiből az egység jön. */
    emitConstWhenUnit?: string;
    property?: string;
    /** Darabszám-vezérelt elosztás: {wrapper property-kulcs, a feloldott modell} — ebből állnak elő az entryk. */
    distribution?: { property: string; model: TItemsModel };
    visibleWhen?: TVisibleWhen;
    effects?: TParamEffect[];
};

const ParametersProcessor: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const dataContext = React.useContext(DatabaseContext);
    const [parameters, setParameters] = React.useState<TParameters>({});
    const parametersRef = React.useRef<TParameters>(parameters);
    const paramKeyMapRef = React.useRef<Record<string, string>>({});
    // Paraméter-név → a Unity-stylescripthez szükséges METAADAT, egyetlen mapben (a korábbi külön
    // relations- és apply_index-refek helyett). A build-effekt tölti a DB-ből (paramScripts):
    //   - applyIndex: a SetStyleScript JSON-sorrendje (uc_style_product_parameters.apply_index;
    //     default seed a V35 migrációból, de adatból hangolható — lásd buildOrder.ts),
    //   - relations : a Relations-transzform infója ({property, descriptor}) — csak azoknál a
    //     paramoknál, amelyek descriptorában van `relations` (konyhai cabinet railing).
    //   - flatten   : a wrapper property-kulcsa, ha a descriptorban van `flatten: true` (pl. a Railing
    //     composite param — lásd flattenWrapper lejjebb, generateStyleScript-nél).
    // Miért ref és nem a paramScriptsből olvassuk közvetlenül: a `parameters` a VÉGLEGES (postfixolt)
    // névvel kulcsolt, a paramScripts a bázisnévvel — ez a map a postfix+egyeztetést egyszer, a
    // build-effektben végzi el (ahol a végleges nevek amúgy is elkészülnek), így a generateStyleScript
    // O(1)-gyel olvas, nem kell minden Unity-küldésnél újraegyeztetni.
    const paramMetaRef = React.useRef<Record<string, TParamMeta>>({});
    // EA-485: debounce timers keyed by parameter id; on a ref so they survive card unmounts.
    const saveTimers = React.useRef<Record<string, number>>({});

    React.useEffect(() => {
        parametersRef.current = parameters;
    }, [parameters]);

    // DEV: a Unity felé menő build-sorrend (a generateStyleScript kulcs-sorrendje) — a Unity
    // BETÖLTÉSÉTŐL FÜGGETLENÜL logolva, hogy a sorrend akkor is ellenőrizhető legyen, ha a 3D nézet
    // (pl. WebGPU/Linux) nem jön fel (ilyenkor a "[Unity::parameters] SetStyleScript:" log el sem
    // indul). A hiányzó apply_index-ű paramok a végére kerülnek — lásd buildOrder.ts.
    React.useEffect(() => {
        const names = Object.keys(parametersRef.current);
        if (!names.length) return;
        const ordered = orderNamesByApplyIndex(names, applyIndexRecord(paramMetaRef.current));
        console.log(
            "[ParametersProcessor::buildOrder] Unity JSON key-order (apply_index):\n  " +
            ordered.map((n) => `[${paramMetaRef.current[n]?.applyIndex ?? "—"}] ${n}`).join("\n  ")
        );
    }, [parameters]);

    const paramContextValue = React.useMemo(() => {
        /** A property-kulcs feloldása a `parameters`-ben használt (esetleg indexelt) alakra. */
        const resolvePropertyKey = (property: string): string =>
            paramKeyMapRef.current[property] || property;

        /** Egy írás beillesztése a state-be; változatlan értéknél az EREDETI objektumot adja vissza. */
        const writeInto = (params: TParameters, name: string, property: string, value: any): TParameters => {
            const currentParam = params[name] || {};
            // 🔥 deterministic resolution only (NO heuristics)
            const resolvedKey = resolvePropertyKey(property);
            // No-op védelem: ha PONT ugyanazt az értéket írnák vissza (pl. a Kosárba gomb
            // blur-je változatlan mezőnél), NEM gyártunk új state-et — így nem indul
            // felesleges Unity-kör (SetStyleScript) sem.
            if (Object.is(currentParam[resolvedKey], value)) return params;
            return { ...params, [name]: { ...currentParam, [resolvedKey]: value } };
        };

        const updateParameters = (name: string, property: string, value: any) => {
            // ADAT-VEZÉRELT mellékhatások (descriptor `effects`): a felhasználói változás EGY MÁSIK
            // paramétert is beállíthat — pl. a keresztmerevítő bekapcsolása a "Tető kapcsolat"-ot
            // Közéülőre. Melyik szabály él, az CSAK az új értéken múlik (matchingEffects), a tényleges
            // írás viszont a FRISS state-en történik (applyEffect), UGYANEBBEN a frissítésben — így nem
            // indul két Unity-kör, és egy elavult snapshot sem írhat vissza régi mezőt. A mellékhatások
            // nem kaszkádolnak: a cél paraméter saját effects-e már nem fut le.
            const effects = matchingEffects(paramMetaRef.current[name]?.effects, value);

            setParameters((prev: TParameters) => {
                let next = writeInto(prev, name, property, value);
                for (const effect of effects) {
                    next = applyEffect(next, effect, resolvePropertyKey);
                }
                if (next === prev) return prev;

                parametersRef.current = next;
                return next;
            });

            // A mellékhatással érintett paramétereket is elmentjük a felhasználó preferenciái közé
            // (debounce-olt, a friss state-ből olvas) — különben újratöltéskor a két mező elcsúszna.
            // Ha az applyEffect végül nem írt (keepIfIn / már jó érték), a mentés a változatlan értéket
            // teszi vissza: ártalmatlan.
            for (const effect of effects) {
                const script = dataContext.paramScripts?.find(
                    (candidate: IParamScript) =>
                        candidate.parameterName === effect.set.paramName &&
                        candidate.parameterProperty.split("[")[0] === effect.set.property
                );
                if (script) notifyParamChanged(script);
            }
        };

        /**
         * A paraméter-descriptor param-onkénti (nyers) formában, apply_index build-sorrendben — UGYANAZ a
         * sorrend, mint a Unity stylescriptnél, de NEM a Unity-transzform (nincs Relations-merge). A Decor
         * él-mezőit a csoport-alapú kimeneti formára szűrjük (cleanParamEdgeOutput), mint a Unity-nál.
         *
         * `visibleOnly`: a nem teljesülő `visibleWhen`-ű paraméterek KIMARADNAK — ugyanaz a szűrés, mint a
         * Unity-kimenetnél, hogy a kettő ne mondjon mást (pl. kikapcsolt / "Nincs" keresztmerevítőnél a
         * railing dekor és szélesség se a stylescriptben, se itt ne szerepeljen). A preferencia-mentés
         * ellenben a SZŰRETLEN formát használja: egy éppen elrejtett paraméter értékét is el kell tudni
         * menteni (pl. ha a felhasználó a mentés debounce-a alatt vált típust).
         */
        const buildParameterScripts = (visibleOnly: boolean): IParamScript[] => {
            const currentParameters = parametersRef.current;
            const meta = paramMetaRef.current;
            const orderedNames = orderNamesByApplyIndex(
                Object.keys(currentParameters), applyIndexRecord(meta)
            ).filter((name) => !visibleOnly || isParamVisible(currentParameters, meta[name]?.visibleWhen));

            // A visszaadott IParamScript-tömb szintén apply_index-rendben (a DB apply_index oszlopból);
            // `visibleOnly` esetén csak a fenti (látható) paraméterek scriptjeit adjuk ki.
            const includedNames = new Set(orderedNames.map((name) => name.replace(/_\d+$/, "")));
            const paramScripts: IParamScript[] = (dataContext.paramScripts ? [...dataContext.paramScripts] : [])
                .filter((script: IParamScript) => !visibleOnly || includedNames.has(script.parameterName))
                .sort((a, b) => {
                    const END = Number.MAX_SAFE_INTEGER;
                    return (toApplyIndex(a.applyIndex) ?? END) - (toApplyIndex(b.applyIndex) ?? END);
                });

            paramScripts.forEach((script: IParamScript, index: number) => {
                orderedNames.forEach((paramName) => {
                    const paramData = currentParameters[paramName];
                    const baseParamName = paramName.replace(/_\d+$/, "");

                    let scriptProperty = script.parameterProperty;
                    if (
                        script.parameterPropertyIndex !== undefined &&
                        script.parameterPropertyIndex !== null
                    ) {
                        scriptProperty =
                            `${script.parameterProperty}[${script.parameterPropertyIndex}]`;
                    }

                    if (
                        baseParamName === script.parameterName &&
                        script.targetType === (paramData.TargetType ?? null) &&
                        script.targetPath === (paramData.Path ?? null) &&
                        paramData.hasOwnProperty(scriptProperty)
                    ) {
                        const rawValue = paramData[scriptProperty];
                        paramScripts[index] = {
                            ...script,
                            // Decor-szerű érték esetén a csoport-alapú kimeneti formát adjuk vissza
                            // (egyébként változatlanul) — így a descriptor is ezt követi.
                            parameterValue: (rawValue && typeof rawValue === "object" && !Array.isArray(rawValue))
                                ? buildDecorEdgeOutput(rawValue)
                                : rawValue,
                        };
                    }
                });
            });

            return paramScripts;
        };

        const generateParameterScripts = () => {
            const currentParameters = parametersRef.current;
            const meta = paramMetaRef.current;
            const visibleNames = orderNamesByApplyIndex(
                Object.keys(currentParameters), applyIndexRecord(meta)
            ).filter((name) => isParamVisible(currentParameters, meta[name]?.visibleWhen));
            console.log(
                "[ParametersProcessor::generateParameterScripts] CART descriptor (per-param form, apply_index order):",
                JSON.stringify(
                    Object.fromEntries(
                        visibleNames.map((name) => [name, cleanParamEdgeOutput(currentParameters[name])])
                    ),
                    null, 2
                )
            );
            return buildParameterScripts(true);
        };

        // A Unity-nak küldött stylescript (SetStyleScript) generálása — EGYETLEN forrás, React-oldalon.
        // A `parameters` wrapper-objektumokat a Unity-designer által várt kimeneti formára hozza:
        //   1) Decor él-mezők csoport-normalizálása (cleanParamEdgeOutput / buildDecorEdgeOutput),
        //   2) relations-descriptoros paramok (BackPassing + passing Enum-ok) → Relations-entryk
        //      (mapRelationsWrapper — a template a DB-descriptorból jön, nem kódból),
        //   3) az azonos célra (TargetType+Path) mutató Relations-wrapperek EGY wrapperbe fésülése
        //      (mergeRelationsWrappers) → a spec szerinti EGYETLEN
        //      { TargetType:"CabinetFrame", Relations:[Top,Bottom,Back] } forma.
        // A JSON kulcs-SORRENDJE a build-tier szerint (buildOrder.ts): Név → Méret → Kötések →
        // Material → Polc/Fiók → Front. A belső `parameters` állapot és a kosár-descriptor
        // (generateParameterScripts) ettől függetlenül a param-onkénti formát tartja (UI-reconstruction
        // + round-trip). A view (UnityView) csak meghívja és elküldi — logika NEM kerül a view-ba.
        // A Size "auto" mód belső kódolása "<szám>auto" (pl. "1auto"), mert az "auto" unit configja
        // default/min/max = 1 — a szám jelentés nélküli. A Unity-designer viszont CSAK a csupasz
        // "auto"-t érti; a "1auto"-t nem tudja parse-olni → degenerált board-geometria, és EMIATT dob
        // az utána következő Decor/él-felhordás ArgumentOutOfRange / ArgumentNull-t az adott Board-ra.
        // Ezért a Unity-KIMENETBEN a "<szám>auto" alakot csupasz "auto"-ra normalizáljuk. A belső
        // `parameters` állapot és a Size-kártya változatlanul "1auto"-t tart (num=1 + "auto" unit),
        // így a kártya és a kosár-descriptor round-trippel — csak a Unity felé megy a csupasz "auto".
        const AUTO_SIZE_RE = /^\d+(?:\.\d+)?auto$/;
        const normalizeAutoSizes = (w: any): any => {
            if (!w || typeof w !== "object") return w;
            const out: Record<string, any> = {};
            for (const k of Object.keys(w)) {
                const v = w[k];
                out[k] = (typeof v === "string" && AUTO_SIZE_RE.test(v)) ? "auto" : v;
            }
            return out;
        };
        // WORKTOP: the FRONT (user-facing) edge — RightEdgeProduct on a laid-flat worktop — is finished
        // on-site / not banded, so it must NOT be sent to Unity at all (not even ZERO_GUID). Detected via
        // the board's IsWorkTop flag; only a worktop board Decor is touched, and only in the Unity output
        // (the internal `parameters` state and the cart descriptor keep the full edge set).
        const stripWorktopFrontEdge = (w: any): any => {
            if (!w || typeof w !== "object") return w;
            let changed = false;
            const out: Record<string, any> = {};
            for (const k of Object.keys(w)) {
                const v = w[k];
                if (v && typeof v === "object" && !Array.isArray(v) && v.BoardProduct
                    && dataContext.boardData?.findByID(v.BoardProduct)?.IsWorkTop
                    && "RightEdgeProduct" in v) {
                    const decorCopy = { ...v };
                    delete decorCopy.RightEdgeProduct;
                    out[k] = decorCopy;
                    changed = true;
                } else {
                    out[k] = v;
                }
            }
            return changed ? out : w;
        };
        // Composite paramok (pl. Railing {RailingType,RailingWidth}), amelyek descriptorában van
        // `flatten: true`: a wrapper property-kulcsa alatti objektumot a wrapper GYÖKERÉRE tesszük ki
        // (a kulcsot eltávolítva), NEM Relations-entryként (lásd mapRelationsWrapper) — így Unity a
        // {"TargetType":"CabinetFrame","RailingType":"Double","RailingWidth":"100mm"} lapos formát kapja
        // {"TargetType":"CabinetFrame","Railing":{"RailingType":...}} helyett. ADAT-VEZÉRELT: a `flatten`
        // flag a DB-descriptorból jön (lásd IRailingParamTypeDescriptor), nem kódból.
        // Composite mezők ÁTKULCSOLÁSA a Unity-kimenethez (descriptor `emitAs`). A mező a kártyán a
        // SAJÁT kulcsán szerkeszthető, és a `parameters` state-ben meg a kosár-descriptorban is úgy
        // marad — az átírás kizárólag a stylescriptre hat:
        //   `[]`       → a mező kimarad (a fióksín típusa: a többi mezőt vezérli, a 3D-modellnek nincs
        //                jelentése),
        //   több kulcs → ugyanaz az érték mindegyiken kimegy (az "Oldalsó ráhagyás" EGY mező a UI-n,
        //                a Unity viszont `LeftMargin`-t és `RightMargin`-t vár).
        // A `flattenWrapper` ELŐTT fut, tehát a lapos és a beágyazott alaknál is ugyanaz a kód dolgozik
        // (a mezőket a property-kulcs alatti objektumból veszi ki). A `map`-ben nem szereplő mező a
        // saját kulcsán megy ki. ADAT-VEZÉRELT: a leképezés a DB-descriptorból jön (compositeEmitKeyMap).
        // SZÁRMAZTATOTT KIMENETI ÉRTÉK (descriptor `emitFrom`): a paraméter kimenő értékét EGY MÁSIK
        // paraméter állapotából képezzük, minden generáláskor újra. Így a származtatott érték nem tud
        // elcsúszni a forrásától — szemben az `effects`-szel, ami csak változáskor futna.
        // Az érték-sablonban `{Paraméter.Property}` helyettesíthető (mint az `emitEntries` `{i}`-je).
        // Ismeretlen eset → a wrapper VÁLTOZATLAN: a paraméter a saját tárolt értékén megy ki.
        const deriveEmitValue = (
            w: any,
            info: { property: string; rule: NonNullable<IParamTypeDescriptor["emitFrom"]> } | undefined,
            current: TParameters
        ): any => {
            if (!info || !w || typeof w !== "object") return w;
            const raw = current?.[info.rule.paramName]?.[info.rule.property];
            const value = info.rule.field && raw && typeof raw === "object" ? raw[info.rule.field] : raw;
            const source = String(value ?? "");
            const template = info.rule.cases?.[source];
            if (template === undefined) return w;

            // A helyettesítés a sablon MINDEN string-levelén fut, az objektum-eseten belül is — így egy
            // összetett leíró egyetlen mezője is jöhet másik paraméterből (pl. a lábazat visszahúzása).
            const fill = (t: any): any => {
                if (typeof t === "string") {
                    return t.replace(
                        /\{([A-Za-z0-9_]+)\.([A-Za-z0-9_]+)\}/g,
                        (_m: string, param: string, prop: string) => String(current?.[param]?.[prop] ?? "")
                    );
                }
                if (Array.isArray(t)) return t.map(fill);
                if (t && typeof t === "object") {
                    const out: Record<string, any> = {};
                    for (const k of Object.keys(t)) out[k] = fill(t[k]);
                    return out;
                }
                return t;
            };
            return { ...w, [info.property]: fill(template) };
        };
        // SZÁM-ALAKÚ KIMENET (descriptor `emitNumber`): a méret-stringből csak a szám megy ki. A tárolt
        // érték és a kártya változatlan — a mértékegység a Unity-szerződés része, nem az értéké.
        // Értelmezhetetlen értéknél a wrapper VÁLTOZATLAN (nem küldünk 0-t egy hibás érték helyett).
        const emitNumericValue = (w: any, property?: string): any => {
            if (!property || !w || typeof w !== "object" || !(property in w)) return w;
            const num = DimensionUtils.parseDimension(String(w[property] ?? "")).num;
            return isNaN(num) ? w : { ...w, [property]: num };
        };
        const remapEmitKeys = (w: any, emitAs?: { property: string; map: Record<string, string[]> }): any => {
            if (!emitAs || !w || typeof w !== "object") return w;
            const inner = w[emitAs.property];
            if (!inner || typeof inner !== "object") return w;
            const out: Record<string, any> = {};
            for (const k of Object.keys(inner)) {
                const targets = emitAs.map[k];
                if (!targets) { out[k] = inner[k]; continue; }
                for (const target of targets) out[target] = inner[k];
            }
            return { ...w, [emitAs.property]: out };
        };
        // ELOSZTÁS: EGY paraméterből TÖBB entry. A Unity nem tömböt vár, hanem elemenként külön
        // blokkot a saját útvonalával ({"Path":"*.DrawerZone[0]","Height":"200mm"}), ezért ez a
        // paraméter kihagyja a szokásos wrapper-utat (nincs TargetType, nincs flatten) — a descriptor
        // `emitEntries` sablonja írja le a kimenetet, a szabályok pedig a kártyával MEGOSZTOTT
        // distribution.ts-ben vannak. `emitEntries` nélkül üres lista: a paraméter nem megy ki.
        const distributionWrappers = (
            current: TParameters,
            name: string,
            info: { property: string; model: TItemsModel }
        ): any[] => itemEntries(
            info.model,
            current[name]?.[info.property],
            itemCount(current, info.model)
        );

        // ÁLLANDÓ KULCSOK: a Unity-szerződés része, nem a felhasználóé (pl. a fiók oldalánál
        // `"TopMargin":"*"`). Azért descriptor-adat és nem beégetett kulcs, hogy egy újabb ilyen mező
        // ne igényeljen kódmódosítást.
        const addEmitConst = (
            w: any,
            consts?: Record<string, string | number>,
            whenUnit?: string,
            property?: string
        ): any => {
            if (!consts || !w || typeof w !== "object") return w;
            // Egység-kapu: a konstansok csak akkor kellenek, ha a mező ebben az egységben áll (a fiók
            // margói pl. csak mm-es mélységnél — `*`-nál két arányos érték versengene egymással).
            if (whenUnit && DimensionUtils.parseDimension(String(w[property ?? ""] ?? "")).unit !== whenUnit) {
                return w;
            }
            return { ...w, ...consts };
        };

        // EGY ÉRTÉK → TÖBB ÚTVONAL. A Unity egy entryben egy útvonalat címez, a fiók oldalmagassága
        // viszont mindkét oldalra és minden fiókra ugyanaz. Az `emitAs` párja: az egy értéket több
        // KULCSRA viszi egy entryn belül, ez több ENTRYRE, útvonalanként.
        // `countFrom` nélkül az útvonalak szó szerint mennek ki — így fix útvonalú cél is leírható.
        const fanOutPaths = (w: any, spec: TEmitPaths | undefined, current: TParameters): any[] => {
            if (!spec?.paths?.length || !w || typeof w !== "object") return [w];
            const paths = spec.paths
                .filter((p) => typeof p === "string" || isParamVisible(current, p.visibleWhen))
                .map((p) => (typeof p === "string" ? p : p.path));
            if (!paths.length) return [];

            const ref = spec.countFrom;
            if (!ref) return paths.map((path) => ({ ...w, Path: path }));

            const raw = current?.[ref.paramName]?.[ref.property];
            const count = parseInt(String(raw ?? ""), 10);
            // Feloldhatatlan darabszám → egyetlen entry sem: kitalált elemszámra hivatkozó útvonalat
            // küldeni rosszabb, mint nem küldeni semmit ("No matching element found for StyleElement").
            if (isNaN(count) || count <= 0) return [];

            return Array.from({ length: count }, (_unused, index) => index).flatMap((index) =>
                paths.map((path) => ({ ...w, Path: path.replace("{i}", String(index)) }))
            );
        };

        const flattenWrapper = (w: any, key?: string): any => {
            if (!key || !w || typeof w !== "object" || !(key in w)) return w;
            const inner = w[key];
            if (!inner || typeof inner !== "object") return w;
            const out: Record<string, any> = {};
            for (const k of Object.keys(w)) {
                if (k === key) continue;
                out[k] = w[k];
            }
            return { ...out, ...inner };
        };
        const generateStyleScript = (): any[] => {
            const current = parametersRef.current;
            const meta = paramMetaRef.current;
            // A descriptor `visibleWhen`-je a KIMENETRE is szűr, nem csak a kártya renderelésére:
            // amelyik paraméter feltétele nem teljesül, az KI SEM MEGY a Unity-nak. Enélkül olyan
            // elemre hivatkozó entryt küldenénk, ami a hierarchiában nem is létezik — pl. a
            // keresztmerevítő-dekor (Path '*.CabinetFrame.Railing.*') kikapcsolt merevítésnél:
            // "No matching element found for StyleElement" + "Could not apply style ... ParameterName:
            // Decor". Ugyanaz a kiértékelő fut, mint a kártyáknál (paramVisibility.isParamVisible),
            // így a látszó mezők és a kimenet nem csúszhat el egymástól. Amint a feltétel teljesül
            // (pl. Single/Double merevítés), a state változik → a következő SetStyleScript már küldi.
            // A gyökér `emitAs: []` ugyanazt jelenti, mint a composite mezőkön: a paraméter CSAK a UI-é.
            // Enélkül az olyan kapcsoló is kimenne, ami csak más mezők láthatóságát vezérli (`Built`),
            // és a Unity "No StyleApplier found for StyleElement" hibát írna rá.
            const names = orderNamesByApplyIndex(Object.keys(current), applyIndexRecord(meta))
                .filter((name) => !meta[name]?.uiOnly)
                .filter((name) => isParamVisible(current, meta[name]?.visibleWhen));
            // INDEXELT KULCSOK: minden `Név[index]` paraméter a SAJÁT entryjében megy ki — a Unity-spec
            // szerint egy entry PONTOSAN EGY indexelt kulcsot hordoz:
            //     {"TargetType":"FrontZone","Fronts[2]":"Left"}
            //     {"TargetType":"FrontZone","Fronts[0]":"Double"}
            // Tehát az indexelt entryket NEM fésüljük össze. A V62-höz készült mergeIndexedWrappers
            // pont az ellenkezőjét tette, abból a — soha nem validált — feltevésből, hogy a Unity
            // per-entry alkalmaz, így két FrontZone-entryből csak az utóbbi érvényesülne. A gyakorlat
            // ennek a fordítottja: a fésült, KÉT indexelt kulcsot hordozó entryt a Unity nem értelmezi,
            // ezért a fiókos zóna front-váltása (Dupla / Front nélküli) NÉMÁN hatástalan volt. A modul
            // maga megmaradt dokumentációként — lásd mergeIndexedWrappers.ts fejlécét —, de NEM fut.
            //
            // A KULCS-SORRENDRŐL: a spec `TargetType`-ot ír előre, mi a property-t — ez a Unity-nak
            // bizonyítottan mindegy (a `{"Width":"600mm","TargetType":"Cabinet"}` entry helyesen
            // alkalmazódik), ezért a wrapper-építés sorrendjéhez nem nyúlunk.
            // `flatMap`, mert egy paraméter TÖBB entryt is adhat: az elosztásé elemenként egyet.
            return mergeRelationsWrappers(names.flatMap((name) => {
                const distribution = meta[name]?.distribution;
                if (distribution) return distributionWrappers(current, name, distribution);

                // ÜRES VÁLASZTÁS → EGYETLEN ENTRY SEM. Egy objektum-értékű paraméter üres objektuma a
                // „nincs ilyen alkatrész" állapot (a vasalat-választó „Függesztő nélküli" opciója), és
                // a {"TargetType":"CabinetFrame","WallHangers":{}} entry a Unity-nak értelmetlen. Így a
                // be/ki kapcsoló nem külön paraméter: maga az érték alakja mondja meg. Ugyanaz az elv,
                // mint a fanOutPaths feloldhatatlan darabszámánál — inkább semmit, mint üres entryt.
                // Kivétel a descriptor `emitEmpty`: ott az üres objektum maga a „vedd le" utasítás.
                const selection = current[name]?.[meta[name]?.property as string];
                if (!meta[name]?.emitEmpty && selection && typeof selection === "object" && !Array.isArray(selection)
                    && Object.keys(selection).length === 0) return [];

                // Az útvonalakra bontás a Relations-feloldás ELŐTT fut: a `relations.back.byPath`
                // útvonalanként más `Joining`-ot adhat (a többi lépés útvonal-független).
                return fanOutPaths(emitNumericValue(deriveEmitValue(cleanParamEdgeOutput(current[name]), meta[name]?.emitFrom, current), meta[name]?.emitNumber), meta[name]?.emitPaths, current)
                    .map((w) => addEmitConst(stripWorktopFrontEdge(normalizeAutoSizes(flattenWrapper(remapEmitKeys(mapRelationsWrapper(w, meta[name]?.relations, current), meta[name]?.emitAs), meta[name]?.flatten))), meta[name]?.emitConst, meta[name]?.emitConstWhenUnit, meta[name]?.property));
            }));
        };

        // EA-485: after a parameter change, persist it scoped to the current shop/customer/cart (all
        // parameter types), keyed by the named parameter — product-independent. Guests are included
        // (server stores customer NULL + cart-scoped). Debounced per parameter id; the value is read
        // back from generateParameterScripts() so the stored form matches exactly what cart/Unity use.
        const notifyParamChanged = (script: IParamScript) => {
            if (!PrestaGlobals.isLoggedIn && PrestaGlobals.cartId <= 0) return;

            const id = script.id;
            if (!id) return;
            window.clearTimeout(saveTimers.current[id]);
            saveTimers.current[id] = window.setTimeout(() => {
                // SZŰRETLEN forma: egy éppen elrejtett paraméter (pl. a railing szélessége, ha közben
                // "Nincs"-re váltott a típus) értékét is el kell tudni menteni.
                const s = buildParameterScripts(false).find((x) => x.id === id);
                if (!s || s.parameterValue === undefined || s.parameterValue === null) return;
                const val = (typeof s.parameterValue === "object")
                    ? JSON.stringify(s.parameterValue)
                    : String(s.parameterValue);
                // Fire-and-forget, but never silently: a failed save means the customer's default
                // will NOT recall on reload, so leave a trace for debugging.
                PrefsApi.savePref(id, val)
                    .then((r) => {
                        if (r && "error" in r) {
                            console.warn(`[ParametersProcessor] savePref failed for '${s.parameterName}':`, r.error);
                        }
                    })
                    .catch((e) => {
                        console.warn(`[ParametersProcessor] savePref request error for '${s.parameterName}':`, e);
                    });
            }, 600);
        };

        return { parameters, updateParameters, generateParameterScripts, generateStyleScript, notifyParamChanged };
    }, [parameters, dataContext.paramScripts]);

    const checkBoardProduct = (boardProduct: string | undefined) => {
        if (boardProduct) {
            const boardData = dataContext.boardData?.findByID(boardProduct);
            if (!boardData) {
                console.warn(`Board product missing: ${boardProduct}`);
                return dataContext.boardData?.first()?.ID || null;
            }
        }
        return boardProduct;
    };

    const checkEdgeProduct = (edgeProduct: string | undefined) => {
        if (edgeProduct && edgeProduct !== ZERO_GUID) {
            const edgeData = dataContext.edgeData?.findByID(edgeProduct);
            if (!edgeData) {
                console.warn(`Edge product missing: ${edgeProduct}`);
                return dataContext.edgeData?.first()?.ID || ZERO_GUID;
            }
        }
        return edgeProduct;
    };

    const decorFallback = (parameterValue: any): any => {
        const newValue = { ...parameterValue };

        newValue.BoardProduct = checkBoardProduct(newValue.BoardProduct);
        newValue.DuplungProduct = checkBoardProduct(newValue.DuplungProduct);

        newValue.LeftEdgeProduct = checkEdgeProduct(newValue.LeftEdgeProduct);
        newValue.RightEdgeProduct = checkEdgeProduct(newValue.RightEdgeProduct);
        newValue.TopEdgeProduct = checkEdgeProduct(newValue.TopEdgeProduct);
        newValue.BottomEdgeProduct = checkEdgeProduct(newValue.BottomEdgeProduct);

        newValue.VisibleEdgeProduct = checkEdgeProduct(newValue.VisibleEdgeProduct);
        newValue.JointEdgeProduct = checkEdgeProduct(newValue.JointEdgeProduct);
        newValue.HiddenEdgeProduct = checkEdgeProduct(newValue.HiddenEdgeProduct);

        return newValue;
    };

    React.useEffect(() => {
        // Quote scripts can arrive before boards/edges: decor fallback would null them
        if (!dataContext.paramScripts || !dataContext.boardData || !dataContext.edgeData) return;
        const params: TParameters = {};
        // A LAP TABLAMERETEBOL jovo korlat MASODIK MENETBEN fut: a mezo a SAJAT descriptorabol csak
        // annyit tud, hogy melyik dekor-parametert nezze — annak az erteke viszont egy MASIK script,
        // ami ebben a ciklusban meg lehet, hogy nincs feldolgozva. Ezert itt csak osszegyujtjuk.
        const boardBounded: Array<{ name: string; property: string;
                                    units: IUnitsTypeDescriptor | undefined; ref: TBoardRef }> = [];
        // Friss név → Unity-stylescript metaadat (applyIndex + relations) — egyetlen mapben.
        const metaMap: Record<string, TParamMeta> = {};
        dataContext.paramScripts.forEach((originalScript: IParamScript) => {
            const script: IParamScript = {
                ...originalScript,
                parameterValue:
                    typeof originalScript.parameterValue === "object" &&
                    originalScript.parameterValue !== null
                        ? { ...originalScript.parameterValue }
                        : originalScript.parameterValue,
            };

            if (script.parameterType === "Size1D") {
                script.parameterType = "Size";
            }

            // BETÖLTÉSI GUARD: az ELTÁROLT érték (uc_style_parameter_values — a shop-szintű default ÉS a
            // customer/cart-scope-ú felhasználói preferencia is) öngyógyítása a SAJÁT descriptorához,
            // UGYANAZOKKAL a korlátokkal, amiket a kártya érvényesítene szerkesztésnél: min/max, egység-
            // lista, enum-opciók. Enélkül egy utólag szigorított korlát (pl. V54: egyenes munkalap hossza
            // min 1000) alatti régi érték (600mm) változatlanul betöltődne, kimenne a Unity-nak és úgy
            // kerülne a kosárba is. A szabályok és a típus-regiszter: paramGuards.ts. A Size korábbi
            // egység-öngyógyítása (sizeFallback) is ott van, a `reason: "unit"` ágban — a guard ennek a
            // szuperszete (ugyanaz + a numerikus korlátok). A Decor NEM ezen az úton megy: annak saját,
            // adat-LÉTEZÉSRE épülő fallbackja van (lásd lentebb, decorFallback).
            const guarded = guardParameterValue(
                script.parameterType, script.parameterValue, script.typeDescriptor
            );
            script.parameterValue = guarded.value;
            guarded.notes.forEach((note) => console.warn(describeGuardNote(script.parameterName, note)));

            if (script.parameterType === "Decor") {
                //@TODO: RECONSIDER
                // Reziliencia: a Decor értéknek objektumnak KELL lennie. Egy hibás/hiányos érték
                // (pl. nyers board-GUID, ami a szerveroldali json_decode-nál null-ra esett) különben
                // az EGÉSZ configurátort lefagyasztaná (fehér oldal). Objektummá kényszerítjük, így az
                // alábbi default-kitöltők (mintázat-irány, ZERO_GUID élek, decorFallback → első board)
                // egy érvényes default Decort építenek belőle. Valid Decornál ez no-op.
                if (typeof script.parameterValue !== "object" || script.parameterValue === null) {
                    console.warn(
                        `[ParametersProcessor] Decor param '${script.parameterName}' non-object value; ` +
                        `falling back to a default Decor. Raw:`, originalScript.parameterValue
                    );
                    script.parameterValue = {};
                }
                if (script.parameterValue.BoardPatternDirection === undefined) {
                    script.parameterValue.BoardPatternDirection = "Straight";
                }

                // A TÖMÖR gyári él (`EdgeProduct`) kibontása a séma szerinti mezőkre. Az alap-cél a
                // LÁTHATÓ él ott, ahol a kártya kirakja a láthatóság-gombokat (pl. szekrény-korpusz);
                // ahol nincs ilyen csoport (front, hátfal, munkalap, lábazat), ott a négy oldal —
                // különben olyan tokent kapna a bejegyzés, amihez nincs is gomb.
                // UGYANAZ a szabály, mint a reset-gombnál (paramDefaults.decorDefault), hogy az első
                // betöltés és a visszaállítás ne adjon más kiosztást.
                if (script.parameterValue.EdgeProduct !== undefined) {
                    const targets: readonly string[] =
                        (script.typeDescriptor as any)?.edge?.visibilityLabels
                            ? [VISIBLE_EDGE_KEY]
                            : SIDE_KEYS;
                    targets.forEach((key) => {
                        if (script.parameterValue[key] === undefined) {
                            script.parameterValue[key] = script.parameterValue.EdgeProduct;
                        }
                    });
                    delete script.parameterValue.EdgeProduct;
                }

                script.parameterValue.LeftEdgeProduct ||= ZERO_GUID;
                script.parameterValue.RightEdgeProduct ||= ZERO_GUID;
                script.parameterValue.TopEdgeProduct ||= ZERO_GUID;
                script.parameterValue.BottomEdgeProduct ||= ZERO_GUID;

                // A láthatóság-alapú él-mezők a sémában MINDIG léteznek (alap: ZERO_GUID),
                // függetlenül attól, hogy a hozzájuk tartozó gombok ki vannak-e rakva.
                // Így a generált stylescript sémája egységes marad.
                script.parameterValue.VisibleEdgeProduct ||= ZERO_GUID;
                script.parameterValue.JointEdgeProduct ||= ZERO_GUID;
                script.parameterValue.HiddenEdgeProduct ||= ZERO_GUID;

                script.parameterValue = decorFallback(script.parameterValue);

                const board = dataContext.boardData?.findByID(script.parameterValue.BoardProduct);
                if (board && board.EdgeBandable != 1) {
                    ALL_EDGE_KEYS.forEach((key) => delete script.parameterValue[key]);
                }
            }

            if (
                script.parameterPropertyIndex !== undefined &&
                script.parameterPropertyIndex !== null
            ) {
                script.parameterProperty =
                    `${script.parameterProperty}[${script.parameterPropertyIndex}]`;
            }

            const baseProperty = script.parameterProperty.split("[")[0];
            paramKeyMapRef.current[baseProperty] = script.parameterPropertyIndex !== undefined &&
            script.parameterPropertyIndex !== null
                ? `${baseProperty}[${script.parameterPropertyIndex}]`
                : script.parameterProperty;
            let namePostfix = "";
            if (params[script.parameterName] !== undefined) {
                let index = 1;
                while (params[`${script.parameterName}_${index}`] !== undefined) {
                    index++;
                }
                namePostfix = `_${index}`;
            }

            script.parameterName = `${script.parameterName}${namePostfix}`;
            params[script.parameterName] = {
                [script.parameterProperty]: script.parameterValue,
            };

            const boardRef = (script.typeDescriptor as ISizeParamTypeDescriptor | undefined)?.maxFromBoard;
            if (boardRef) {
                boardBounded.push({
                    name: script.parameterName,
                    property: script.parameterProperty,
                    units: (script.typeDescriptor as ISizeParamTypeDescriptor).units,
                    ref: boardRef,
                });
            }

            //  - applyIndex: a JSON build-order
            //  - relations : a Relations-transform inf ({property, descriptor}) — only if `relations`
            //  exists in the descriptor (BackPassing back/fixed & passing Enum entry format).
            //  - flatten   : a wrapper property-key, when a descriptor has `flatten: true` (Railing) key.
            const relations = (script.typeDescriptor as any)?.relations
                ? {
                    property: script.parameterProperty,
                    descriptor: (script.typeDescriptor as any).relations,
                    topOffWhen: (script.typeDescriptor as any).top?.offWhen,
                }
                : undefined;
            const flatten = (script.typeDescriptor as any)?.flatten
                ? script.parameterProperty
                : undefined;
            // Composite mezők kimeneti átkulcsolása (`emitAs`) — csak a stylescriptre hat.
            const emitKeyMap = itemsEmitKeyMap(script.typeDescriptor);
            const itemsModel = resolveItems(script.typeDescriptor);
            const distribution = itemsModel?.source === "count"
                ? { property: script.parameterProperty, model: itemsModel }
                : undefined;
            metaMap[script.parameterName] = {
                applyIndex: toApplyIndex(script.applyIndex),
                relations,
                flatten,
                emitAs: Object.keys(emitKeyMap).length
                    ? { property: script.parameterProperty, map: emitKeyMap }
                    : undefined,
                uiOnly: Array.isArray((script.typeDescriptor as any)?.emitAs)
                    && !(script.typeDescriptor as any).emitAs.length,
                emitFrom: script.typeDescriptor?.emitFrom
                    ? { property: script.parameterProperty, rule: script.typeDescriptor.emitFrom }
                    : undefined,
                emitNumber: script.typeDescriptor?.emitNumber ? script.parameterProperty : undefined,
                emitPaths: script.typeDescriptor?.emitPaths,
                emitConst: script.typeDescriptor?.emitConst,
                emitEmpty: script.typeDescriptor?.emitEmpty,
                emitConstWhenUnit: script.typeDescriptor?.emitConstWhenUnit,
                property: script.parameterProperty,
                distribution,
                // A kimeneti feltétel (ugyanaz a descriptor-mező, amivel a kártya is szűr) —
                // lásd generateStyleScript.
                visibleWhen: script.typeDescriptor?.visibleWhen,
                // Mellékhatás-szabályok: ennek a paraméternek a változása MÁS paramétert is beállíthat
                // (lásd paramEffects.ts, updateParameters).
                effects: script.typeDescriptor?.effects,
            };

            if (script.targetType !== null) {
                params[script.parameterName].TargetType = script.targetType;
            }

            if (script.targetPath !== null) {
                params[script.parameterName].Path = script.targetPath;
            }

            if (
                script.parameterProperty === "Name" &&
                script.targetPath === "" &&
                script.targetType === null
            ) {
                params[script.parameterName].Name = PrestaGlobals.productName;
            }
        });

        // MASODIK MENET — a lap tablameretebol jovo felso korlat ervenyesitese a BETOLTOTT ertekre.
        // A sima betoltesi guard (guardParameterValue) csak a mezo SAJAT descriptorat latja, a
        // lapkatalogust es a tobbi parametert nem, ezert egy regen mentett — es azota tulmeretesse valt
        // — ertek (pl. egy 2900 mm szeles frontlap 2070-es tablan) valtozatlanul betoltodott, kiment a
        // Unity-nak es igy kerult a kosarba is, holott a kartyan ma mar be sem lehetne vinni.
        // Ugyanaz a ket fuggveny fut, mint a kartyan: `boardBoundMax` adja a hatart, `guardDimension`
        // szoritja be — igy a betoltott ertek es a szerkesztheto tartomany nem mondhat mast.
        boardBounded.forEach(({ name, property, units, ref }) => {
            const value = params[name]?.[property];
            const unit = DimensionUtils.parseDimension(String(value ?? "")).unit;
            const max = boardBoundMax(params, dataContext.boardData, ref, unit);
            if (max === undefined) return;

            const guarded = guardDimension(units, value, { max });
            if (guarded.value === value) return;
            params[name][property] = guarded.value;
            guarded.notes.forEach((note) => console.warn(describeGuardNote(name, note)));
        });

        paramMetaRef.current = metaMap;
        // Sync the ref SYNCHRONOUSLY here — NOT only via the `[parameters]` effect near the top.
        // UnityView is a DESCENDANT of this component, so React flushes its SetStyleScript effect
        // (which calls generateStyleScript() → reads parametersRef.current) BEFORE this component's
        // ancestor ref-sync effect in the very commit that first populates `parameters`. Without this
        // line the first generateStyleScript() reads an EMPTY parametersRef → sends SetStyleScript("[]")
        // → the cabinet "falls apart" on first load (a refresh only masks it via warm caches / faster
        // params fetch). This assignment is confined to the build effect (runs only when paramScripts
        // change), so it never clobbers the ref-advance that updateParameters() does on autoedge/user
        // changes — hence NO autoedging regression (the reason the render-time sync was reverted).
        parametersRef.current = params;
        setParameters(params);
    }, [dataContext.paramScripts, dataContext.boardData, dataContext.edgeData]);

    return (
        <ParametersContext.Provider value={paramContextValue}>
            {children}
        </ParametersContext.Provider>
    );
};

export default ParametersProcessor;
